import { useState } from 'react';
import { c, fmtShort } from '../theme';

const BRAND_GRADIENT =
  'linear-gradient(100deg,#6A2BD9 0%,#E0392E 28%,#E22A8C 55%,#5A3BE0 78%,#2BB3E6 100%)';

/** "2023-04" → "APR 2023" */
const capturedLabel = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const [year, month] = iso.split('-');
  const names = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const name = names[Number(month) - 1];
  return name ? `${name} ${year}` : year;
};

/**
 * Full-bleed 220px band showing Street View for the address.
 *
 * The image is proxied by the Worker, so no API key reaches the page. Where
 * Google has no outdoor imagery — and while the photo loads — the brand
 * gradient carries the band rather than a broken frame.
 */
export function PhotoBand({
  photoUrl,
  badge,
  captured,
  address,
}: {
  photoUrl: string | null;
  badge: number | null;
  captured?: string | null;
  address?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const showPhoto = !!photoUrl && !failed;
  const taken = capturedLabel(captured);

  return (
    <div style={{ position: 'relative', height: 220, overflow: 'hidden', background: BRAND_GRADIENT }}>
      {showPhoto && (
        <img
          src={`${photoUrl}?w=640&h=440`}
          alt={address ? `Street View of ${address}` : 'Street View of the property'}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: loaded ? 1 : 0,
            transition: 'opacity .4s ease',
          }}
        />
      )}

      {/* cream dot grid, fading in from both edges */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(245,238,219,0.55) 0.8px, transparent 1.2px)',
          backgroundSize: '7px 7px',
          WebkitMaskImage:
            'linear-gradient(90deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.9) 100%)',
          maskImage:
            'linear-gradient(90deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 45%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.9) 100%)',
          // Over a photograph the grid competes with the subject.
          opacity: loaded ? 0.35 : 1,
          transition: 'opacity .4s ease',
        }}
      />

      {/* red → blue edge tint over a bottom fade to ink */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: loaded
            ? 'linear-gradient(90deg, rgba(214,58,46,0.18) 0%, rgba(214,58,46,0) 35%, rgba(43,179,230,0) 65%, rgba(58,59,214,0.18) 100%), linear-gradient(180deg, rgba(31,26,31,0) 0%, rgba(31,26,31,0.1) 55%, rgba(31,26,31,0.6) 100%)'
            : 'linear-gradient(90deg, rgba(214,58,46,0.35) 0%, rgba(214,58,46,0) 35%, rgba(43,179,230,0) 65%, rgba(58,59,214,0.35) 100%), linear-gradient(180deg, rgba(31,26,31,0) 0%, rgba(31,26,31,0.1) 55%, rgba(31,26,31,0.6) 100%)',
          transition: 'background .4s ease',
        }}
      />

      {/* A photo years old can mislead a buyer, so the band says when it was taken. */}
      {showPhoto && loaded && taken && (
        <div
          style={{
            position: 'absolute',
            left: 22,
            top: 18,
            fontSize: 8,
            letterSpacing: '0.16em',
            fontWeight: 600,
            color: 'rgba(245,238,219,0.85)',
            textShadow: '0 1px 3px rgba(31,26,31,0.5)',
            whiteSpace: 'nowrap',
          }}
        >
          STREET VIEW · {taken}
        </div>
      )}

      {badge != null && (
        <svg
          width="64"
          height="64"
          viewBox="0 0 64 64"
          fill="none"
          style={{ position: 'absolute', right: 22, bottom: 18, pointerEvents: 'none' }}
          aria-hidden
        >
          <circle
            cx="32"
            cy="32"
            r="28"
            stroke="rgba(245,238,219,0.9)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="0.1 5.6"
            transform="rotate(-90 32 32)"
          />
          <text
            x="32"
            y="36"
            textAnchor="middle"
            fontFamily="Archivo, sans-serif"
            fontSize="11"
            fontWeight="700"
            fill={c.cream}
          >
            {fmtShort(badge)}
          </text>
        </svg>
      )}
    </div>
  );
}
